/**
 * The hover actions on a classic message row — copy link, edit, pin, delete.
 *
 * A pill that hovers over the row. Absolutely positioned and `invisible` until hover, so it
 * costs no layout and never pushes the body around.
 *
 * `invisible` rather than `hidden` is load-bearing: every button in this bar is a tooltip
 * trigger, and a trigger with no box is a tooltip with no anchor. `_libs/message-hover.ts`
 * has the two rules and why each one is.
 *
 * This is the CLASSIC UI's bar, used by `ChannelMessageItem` and the pinned strip. The
 * conversation UI draws Figma's grouped `ButtonGroup` instead and keeps it private — see
 * `channel-message-action-bar.tsx`.
 */

import { cn } from "cn";
import { kebabCase } from "lodash-es";
import { Button } from "@/components/ui/button/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MESSAGE_HOVER_VISIBLE } from "../_libs/message-hover";
import type { ChannelMessageAction } from "../_types/channel-surface";

type ChannelMessageActionsProps = {
  actions: ChannelMessageAction[];
  className?: string;
};

export function ChannelMessageActions({
  actions,
  className,
}: ChannelMessageActionsProps) {
  if (actions.length === 0) return null;

  return (
    <div
      data-test="channel-message-actions"
      className={cn(
        /* sorted */
        "absolute z-4 right-6 top-3",
        "invisible",
        "has-data-[state=open]:visible",
        MESSAGE_HOVER_VISIBLE,
        className,
      )}
    >
      <div
        className={cn(
          /* sorted */
          "flex items-center",
          "overflow-hidden",
          "bg-bg-light border border-border-dark-low divide-x divide-border-dark-low rounded-full shadow-sm",
        )}
      >
        {actions.map((action) => (
          <Tooltip key={kebabCase(action.title)}>
            <TooltipTrigger asChild>
              <Button
                size="icon-md"
                kind="ghost"
                variant="neutral"
                className="rounded-none"
                data-test={kebabCase(action.title)}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  action.onClick(event);
                }}
              >
                <action.icon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{action.title}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
